import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import PhoneNumberModal from '../components/PhoneNumberModal';

// Backend API URL - configurable per environment
// Preferred: REACT_APP_PUBLIC_API_URL
// Fallback: production URL for prod builds, localhost for dev
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

// Do NOT set a global axios baseURL here. We rely on the dev proxy (or
// relative paths) so requests work in Docker and on localhost. OAuth
// redirects still use BACKEND_URL explicitly where needed.

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check for token in URL parameters (from Google OAuth callback)
  // Security: Remove token from URL immediately to prevent exposure in logs/history
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      // Clean up URL immediately before setting token to prevent exposure
      window.history.replaceState({}, document.title, window.location.pathname);
      // Set token in localStorage first
      localStorage.setItem('authToken', tokenFromUrl);
      // Then set token in state - this will trigger the useEffect to fetch user data
      setToken(tokenFromUrl);
      // Force a small delay to ensure state updates before fetching
      // The token change will trigger the fetchUser useEffect below
    }
  }, []);

  // State for phone number requirement
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Fetch user data when token is available
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        setLoading(true);
        try {
          // Set authorization header before making request
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          const response = await axios.get(`${BACKEND_URL}/api/user`);
          if (response.data.success) {
            const userData = response.data.user;
            setUser(userData);
            
            // Show phone number modal only if:
            // 1. User doesn't have phone number (check both hasPhoneNumber flag and actual phone fields)
            // 2. We're not on the payment status page (let payment flow complete first)
            const isPaymentStatusPage = window.location.pathname === '/payment-status';
            const hasPhone = userData.hasPhoneNumber || (userData.countryCode && userData.phoneNumber);
            const shouldShowModal = !hasPhone && !isPaymentStatusPage;
            
            // console.log('📱 Phone modal check:', {
            //   hasPhoneNumber: userData.hasPhoneNumber,
            //   countryCode: userData.countryCode,
            //   phoneNumber: userData.phoneNumber,
            //   hasPhone: hasPhone,
            //   isPaymentStatusPage: isPaymentStatusPage,
            //   shouldShowModal: shouldShowModal
            // });
            
            if (shouldShowModal) {
              setShowPhoneModal(true);
            } else {
              setShowPhoneModal(false);
            }
          } else {
            // If response is not successful, clear token
            setToken(null);
            localStorage.removeItem('authToken');
            setUser(null);
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          // Token might be invalid, clear it
          setToken(null);
          localStorage.removeItem('authToken');
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        } finally {
          setLoading(false);
        }
      } else {
        // No token, clear user and set loading to false
        setUser(null);
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  // Login function - redirects to Google OAuth endpoint on backend
  const login = async () => {
    try {
      // Check if OAuth is configured before redirecting
      const statusResponse = await axios.get(`${BACKEND_URL}/api/auth/status`);

      if (!statusResponse.data.configured) {
        alert(statusResponse.data.message || 'Google OAuth is not configured. Please set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in your environment variables.');
        return;
      }

      // OAuth is configured, proceed with redirect
      window.location.href = `${BACKEND_URL}/auth/google`;
    } catch (error) {
      console.error('Error checking OAuth status:', error);
      // If status check fails, try redirecting anyway (might be network issue)
      window.location.href = `${BACKEND_URL}/auth/google`;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user && !!token;
  };

  // Handle phone number submission success
  const handlePhoneSuccess = (updatedUser, bonusCredits) => {
    setUser(updatedUser);
    setShowPhoneModal(false);
    // User data is already updated in the response
    // Wallet will be refreshed automatically on next fetchUserData call
  };

  // Handle skip phone number
  const handleSkipPhone = () => {
    setShowPhoneModal(false);
    // Allow user to proceed without phone number
  };

  // Fetch user data function - can be called to refresh user info
  const fetchUserData = async () => {
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/user`);
        if (response.data.success) {
          const userData = response.data.user;
          setUser(userData);
          return userData;
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        logout();
      }
    }
    return null;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    setUser, // Expose setUser for phone modal and admin panel to update user state
    fetchUserData // Expose fetchUserData to refresh user data
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showPhoneModal && (
        <PhoneNumberModal
          isOpen={showPhoneModal}
          onClose={handleSkipPhone}
          onSuccess={handlePhoneSuccess}
          onSkip={handleSkipPhone}
        />
      )}
    </AuthContext.Provider>
  );
};
